/**
 * Authentication Context
 * 
 * Provides global authentication state and methods
 * Production-ready with proper error handling and persistence
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../lib/api/auth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /**
     * Load user from cache or API
     */
    const loadUser = useCallback(async () => {
        try {
            // Issue #196: the real token is an HttpOnly cookie now, which
            // this JS can't read to gate on the way it used to gate on
            // localStorage's access_token. The cached user object doubles as
            // that same cheap "don't bother calling /me" hint — it's set
            // right alongside the cookie on every login and cleared on
            // logout/auth-failure, so its absence means the same thing the
            // missing token used to mean.
            const cachedUser = authService.getCachedUser();
            if (!cachedUser) {
                setLoading(false);
                return;
            }

            // Load from cache first for instant UI
            setUser(cachedUser);

            // Fetch fresh user data — this is the real check; the cookie is
            // what actually authenticates it, not the cache above.
            const userData = await authService.getCurrentUser();
            setUser(userData);
            setError(null);
        } catch (err) {
            console.error('Failed to load user:', err);

            // The session can't be verified for ANY reason (invalid/expired
            // token, or the fetch itself failing) — clear it and drop back to
            // signed-out rather than leaving the optimistic cached user in
            // place indefinitely. Previously this only cleared on an exact
            // 401, so any other failure (network hiccup, a token from a
            // different backend/DB state, etc.) left the UI stuck showing a
            // stale "logged in" user with no way back to a clean login screen
            // short of manually clearing browser storage.
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            setUser(null);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Initialize auth state on mount
     */
    useEffect(() => {
        loadUser();
    }, [loadUser]);

    /**
     * Login user
     * @param {string} email - User email
     * @param {string} password - User password
     * @returns {Promise<Object>} User data
     */
    const login = async (email, password, turnstileToken) => {
        try {
            setError(null);
            const data = await authService.login({ email, password, turnstileToken });
            setUser(data.user);
            return data;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    /**
     * Register new user
     * @param {Object} userData - Registration data
     * @returns {Promise<Object>} User data
     */
    const register = async (userData) => {
        try {
            setError(null);
            // register() now returns auth tokens directly and authService stores
            // them (the Turnstile token is single-use, so we can't call the gated
            // /login right after). Hydrate the full user profile via /auth/me.
            const regData = await authService.register(userData);
            if (regData?.access_token) {
                const currentUser = await authService.getCurrentUser();
                setUser(currentUser);
                return regData;
            }
            // Fallback for an older backend that returns only the user object.
            const loginData = await login(userData.email, userData.password, userData.turnstileToken);
            return loginData;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    /**
     * Logout user. authService.logout() is async now (issue #196) — it has
     * to make a real backend call to revoke the session and clear the
     * HttpOnly cookies, then hard-redirects to /login itself; the state
     * resets below happen first so the UI doesn't sit "logged in" during
     * that request.
     */
    const logout = useCallback(() => {
        setUser(null);
        setError(null);
        authService.logout();
    }, []);

    /**
     * Update user profile
     * @param {Object} updates - Profile updates
     * @returns {Promise<Object>} Updated user data
     */
    const updateUser = async (updates) => {
        try {
            setError(null);
            const updatedUser = await authService.updateProfile(updates);
            setUser(updatedUser);
            return updatedUser;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    /**
     * Refresh user data
     * Useful after user performs actions that might change their quota
     */
    // Memoized on purpose: AuthCallback lists this in its effect deps, and a
    // fresh identity per render is what re-fired that effect after a
    // successful OAuth exchange (see AuthCallback.js). setUser is stable.
    const refreshUser = useCallback(async () => {
        try {
            const userData = await authService.getCurrentUser();
            setUser(userData);
            return userData;
        } catch (err) {
            console.error('Failed to refresh user:', err);
            throw err;
        }
    }, []);

    const value = {
        user,
        loading,
        error,
        login,
        register,
        logout,
        updateUser,
        refreshUser,
        isAuthenticated: !!user,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

/**
 * Hook to use auth context
 * @returns {Object} Auth context value
 */
export function useAuth() {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }

    return context;
}

export default AuthContext;
